import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Typography,
  Autocomplete,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from '../config/dayjs';
import { useCheque, useAuth, useNotification } from '../context';
import * as catalogoService from '../services/catalogoService';
import * as chequeService from '../services/chequeService';

// Esquema de validación con Yup
const chequeSchema = yup.object().shape({
  numero: yup
    .string()
    .required('El número de cheque es requerido')
    .trim(),
  bancoId: yup
    .string()
    .required('El banco es requerido'),
  numeroCuentaOrigen: yup
    .string()
    .required('El número de cuenta origen es requerido')
    .trim(),
  clienteNombre: yup
    .string()
    .required('El nombre del cliente es requerido')
    .trim(),
  nombreDuenoCuenta: yup
    .string()
    .required('El nombre del dueño de la cuenta es requerido')
    .trim(),
  monto: yup
    .number()
    .required('El monto es requerido')
    .positive('El monto debe ser mayor a 0')
    .typeError('El monto debe ser un número'),
  fechaEmision: yup
    .mixed()
    .required('La fecha de emisión es requerida')
    .test('is-valid-date', 'Fecha inválida', (value) => {
      return value && dayjs(value).isValid();
    })
    .test('max-date', 'La fecha de emisión no puede ser mayor a hoy', (value) => {
      return value && dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  fechaPago: yup
    .mixed()
    .required('La fecha de pago es requerida')
    .test('is-valid-date', 'Fecha inválida', (value) => {
      return value && dayjs(value).isValid();
    })
    .test('min-date', 'La fecha de pago debe ser mayor o igual a la fecha de emisión', function(value) {
      const { fechaEmision } = this.parent;
      if (!value || !fechaEmision) return true;
      return dayjs(value).isSameOrAfter(dayjs(fechaEmision), 'day');
    }),
  esCruzado: yup.boolean(),
  esEndosado: yup.boolean(),
  esPosfechado: yup.boolean(),
  motivoRecepcion: yup
    .string()
    .required('El motivo de recepción es requerido')
    .oneOf(['pago_factura', 'anticipo_venta', 'cancelacion_cuenta'], 'Motivo inválido'),
  facturasRelacionadas: yup.string().when('motivoRecepcion', {
    is: 'pago_factura',
    then: (schema) => schema.required('Las facturas relacionadas son requeridas cuando el motivo es pago de factura'),
    otherwise: (schema) => schema.notRequired(),
  }),
  observaciones: yup.string(),
});

const CheckRegistrationPage = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { createCheque, loading: chequeLoading } = useCheque();
  const { user } = useAuth();
  const { showWarning, showSuccess, showError } = useNotification();

  const [bancos, setBancos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [showClienteAlert, setShowClienteAlert] = useState(false);
  const [loadingCheque, setLoadingCheque] = useState(isEdit);
  const [isEditDisabled, setIsEditDisabled] = useState(false);
  const [disabledReason, setDisabledReason] = useState('');

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(chequeSchema),
    defaultValues: {
      numero: '',
      bancoId: '',
      numeroCuentaOrigen: '',
      clienteNombre: '',
      nombreDuenoCuenta: '',
      monto: '',
      fechaEmision: dayjs(),
      fechaPago: dayjs(),
      esCruzado: false,
      esEndosado: false,
      esPosfechado: false,
      motivoRecepcion: '',
      facturasRelacionadas: '',
      observaciones: '',
    },
  });

  // Watch para detectar cambios
  const watchFechaPago = watch('fechaPago');
  const watchClienteNombre = watch('clienteNombre');
  const watchNombreDueno = watch('nombreDuenoCuenta');
  const watchMotivoRecepcion = watch('motivoRecepcion');

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        setLoadingCatalogos(true);
        const [bancosData, clientesData] = await Promise.all([
          catalogoService.getBancos({ activo: true }),
          catalogoService.getClientes({ activo: true }),
        ]);

        console.log('Bancos cargados:', bancosData);
        console.log('Clientes cargados:', clientesData);

        setBancos(bancosData);
        setClientes(clientesData);
      } catch (error) {
        console.error('Error cargando catálogos:', error);
      } finally {
        setLoadingCatalogos(false);
      }
    };

    loadCatalogos();
  }, []);

  // Cargar datos del cheque si está en modo edición
  useEffect(() => {
    if (isEdit && id) {
      const loadCheque = async () => {
        try {
          setLoadingCheque(true);
          const response = await chequeService.getChequeById(id);
          const cheque = response.data || response;

          // Cargar los valores al formulario
          setValue('numero', cheque.numero_cheque || '');
          setValue('bancoId', cheque.banco_id?.toString() || '');
          setValue('numeroCuentaOrigen', cheque.numero_cuenta_origen || '');
          setValue('clienteNombre', cheque.cliente?.nombre || '');
          setValue('nombreDuenoCuenta', cheque.nombre_dueno_cuenta || '');
          setValue('monto', cheque.monto || '');
          setValue('fechaEmision', dayjs(cheque.fecha_emision));
          setValue('fechaPago', dayjs(cheque.fecha_pago));
          setValue('esCruzado', cheque.es_cruzado || false);
          setValue('esEndosado', cheque.es_endosado || false);
          setValue('esPosfechado', cheque.es_postfechado || false);
          setValue('motivoRecepcion', cheque.motivo_recepcion || '');
          setValue('facturasRelacionadas', cheque.facturas_relacionadas || '');
          setValue('observaciones', cheque.observaciones || '');

          // Verificar si el usuario es cajero y la fecha de emisión no es hoy
          if (user?.rol === 'cajero') {
            const fechaEmision = dayjs(cheque.fecha_emision);
            const hoy = dayjs().startOf('day');

            if (!fechaEmision.isSame(hoy, 'day')) {
              setIsEditDisabled(true);
              setDisabledReason('Los cajeros solo pueden modificar cheques cuya fecha de emisión sea el día actual.');
            }
          }
        } catch (error) {
          console.error('Error al cargar el cheque:', error);
          showError('Error al cargar los datos del cheque');
          navigate('/cheques/lista');
        } finally {
          setLoadingCheque(false);
        }
      };

      loadCheque();
    }
  }, [isEdit, id, setValue, navigate, showError, user]);

  // Auto-detectar post-fechado
  useEffect(() => {
    if (watchFechaPago) {
      const hoy = dayjs().startOf('day');
      const fechaPago = dayjs(watchFechaPago).startOf('day');

      setValue('esPosfechado', fechaPago.isAfter(hoy));
    }
  }, [watchFechaPago, setValue]);

  // Manejar copia de nombre cliente a nombre dueño cuenta al perder foco
  const handleClienteBlur = () => {
    const clienteNombre = watchClienteNombre;
    const duenoNombre = watchNombreDueno;

    // Solo copiar si el cliente tiene valor y el dueño está vacío
    if (clienteNombre && clienteNombre.trim() && (!duenoNombre || duenoNombre.trim() === '')) {
      setValue('nombreDuenoCuenta', clienteNombre.trim());
    }
  };

  // Validar nombre dueño cuenta vs cliente
  useEffect(() => {
    if (watchClienteNombre && watchNombreDueno) {
      const clienteNombre = watchClienteNombre.toLowerCase().trim();
      const duenoNombre = watchNombreDueno.toLowerCase().trim();
      setShowClienteAlert(clienteNombre !== duenoNombre);
    } else {
      setShowClienteAlert(false);
    }
  }, [watchClienteNombre, watchNombreDueno]);

  const onSubmit = async (data) => {
    try {
      // Preparar datos para enviar (snake_case para backend)
      const chequeData = {
        numero_cheque: data.numero,
        banco_id: data.bancoId,
        numero_cuenta_origen: data.numeroCuentaOrigen,
        cliente_nombre: data.clienteNombre,
        nombre_dueno_cuenta: data.nombreDuenoCuenta,
        monto: parseFloat(data.monto),
        fecha_emision: dayjs(data.fechaEmision).format('YYYY-MM-DD'),
        fecha_pago: dayjs(data.fechaPago).format('YYYY-MM-DD'),
        es_cruzado: data.esCruzado,
        es_endosado: data.esEndosado,
        es_postfechado: data.esPosfechado,
        cajero_id: user?.id,
        motivo_recepcion: data.motivoRecepcion,
        facturas_relacionadas: data.facturasRelacionadas || undefined,
        observaciones: data.observaciones || undefined,
      };

      if (isEdit && id) {
        await chequeService.updateCheque(id, chequeData);
        showSuccess('Cheque actualizado correctamente');
      } else {
        await createCheque(chequeData);
      }

      navigate('/cheques/lista');
    } catch (error) {
      console.error('Error al guardar cheque:', error);
      showError(isEdit ? 'Error al actualizar el cheque' : 'Error al crear el cheque');
    }
  };

  const handleCancel = () => {
    navigate('/cheques/lista');
  };

  if (loadingCatalogos || loadingCheque) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? 'Editar Cheque' : 'Registrar Nuevo Cheque'}
        </Typography>

        {/* Alerta cuando la edición está deshabilitada */}
        {isEditDisabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {disabledReason}
          </Alert>
        )}

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {/* Número de Cheque */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="numero"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Número de Cheque"
                        fullWidth
                        required
                        disabled={isEditDisabled}
                        error={!!errors.numero}
                        helperText={errors.numero?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Banco */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="bancoId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth required error={!!errors.bancoId} disabled={isEditDisabled}>
                        <InputLabel id="banco-select-label">Banco</InputLabel>
                        <Select
                          labelId="banco-select-label"
                          id="banco-select"
                          value={value || ''}
                          label="Banco"
                          disabled={isEditDisabled}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            console.log('Banco seleccionado:', newValue);
                            onChange(newValue);
                          }}
                        >
                          <MenuItem value="">
                            <em>Seleccione un banco</em>
                          </MenuItem>
                          {bancos.map((banco) => (
                            <MenuItem key={banco.id} value={banco.id}>
                              {banco.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.bancoId && (
                          <Typography variant="caption" color="error">
                            {errors.bancoId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Número de Cuenta Origen */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="numeroCuentaOrigen"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Número de Cuenta Origen"
                        fullWidth
                        required
                        disabled={isEditDisabled}
                        error={!!errors.numeroCuentaOrigen}
                        helperText={errors.numeroCuentaOrigen?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Cliente */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="clienteNombre"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        freeSolo
                        disabled={isEditDisabled}
                        options={clientes}
                        getOptionLabel={(option) => {
                          // option can be a string (typed text) or an object (selected from list)
                          if (typeof option === 'string') return option;
                          return option.nombre || '';
                        }}
                        value={value}
                        onChange={(_, newValue) => {
                          // newValue can be a string (typed) or object (selected)
                          if (typeof newValue === 'string') {
                            onChange(newValue);
                          } else if (newValue) {
                            onChange(newValue.nombre);
                          } else {
                            onChange('');
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          // Update on input change for free text
                          onChange(newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cliente"
                            required
                            error={!!errors.clienteNombre}
                            helperText={errors.clienteNombre?.message || 'Escriba el nombre del cliente o seleccione uno existente'}
                            onBlur={handleClienteBlur}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                {/* Nombre del Dueño de la Cuenta */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="nombreDuenoCuenta"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nombre del Dueño de la Cuenta"
                        fullWidth
                        required
                        disabled={isEditDisabled}
                        error={!!errors.nombreDuenoCuenta}
                        helperText={errors.nombreDuenoCuenta?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Monto */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="monto"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Monto"
                        type="number"
                        fullWidth
                        required
                        disabled={isEditDisabled}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        error={!!errors.monto}
                        helperText={errors.monto?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Fecha de Emisión */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="fechaEmision"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Fecha de Emisión"
                        maxDate={dayjs()}
                        disabled={isEditDisabled}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            error: !!errors.fechaEmision,
                            helperText: errors.fechaEmision?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Fecha de Pago */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="fechaPago"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Fecha de Pago"
                        disabled={isEditDisabled}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            error: !!errors.fechaPago,
                            helperText: errors.fechaPago?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Checkboxes */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Controller
                      name="esCruzado"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={field.value} disabled={isEditDisabled} />}
                          label="Es Cruzado"
                        />
                      )}
                    />
                    <Controller
                      name="esEndosado"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={field.value} disabled={isEditDisabled} />}
                          label="Es Endosado"
                        />
                      )}
                    />
                    <Controller
                      name="esPosfechado"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={field.value} disabled />}
                          label="Es Post-fechado (auto-detectado)"
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* Cajero (readonly) */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cajero"
                    fullWidth
                    value={user?.nombre || ''}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                {/* Motivo de Recepción */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="motivoRecepcion"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth required error={!!errors.motivoRecepcion} disabled={isEditDisabled}>
                        <InputLabel>Motivo de Recepción</InputLabel>
                        <Select {...field} label="Motivo de Recepción" disabled={isEditDisabled}>
                          <MenuItem value="">
                            <em>Seleccione un motivo</em>
                          </MenuItem>
                          <MenuItem value="pago_factura">Pago de Factura</MenuItem>
                          <MenuItem value="anticipo_venta">Anticipo de Venta</MenuItem>
                          <MenuItem value="cancelacion_cuenta">Cancelación de Cuenta</MenuItem>
                        </Select>
                        {errors.motivoRecepcion && (
                          <Typography variant="caption" color="error">
                            {errors.motivoRecepcion.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Facturas Relacionadas (condicional) */}
                {watchMotivoRecepcion === 'pago_factura' && (
                  <Grid item xs={12}>
                    <Controller
                      name="facturasRelacionadas"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Facturas Relacionadas"
                          fullWidth
                          required
                          disabled={isEditDisabled}
                          multiline
                          rows={2}
                          placeholder="Ej: 001-00001234, 001-00001235"
                          error={!!errors.facturasRelacionadas}
                          helperText={errors.facturasRelacionadas?.message}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Observaciones */}
                <Grid item xs={12}>
                  <Controller
                    name="observaciones"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Observaciones"
                        fullWidth
                        disabled={isEditDisabled}
                        multiline
                        rows={3}
                        error={!!errors.observaciones}
                        helperText={errors.observaciones?.message}
                      />
                    )}
                  />
                </Grid>

                {/* Alert si nombre no coincide */}
                {showClienteAlert && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      El nombre del dueño de la cuenta no coincide con el nombre del cliente seleccionado.
                      Por favor, verifique la información.
                    </Alert>
                  </Grid>
                )}

                {/* Botones */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={chequeLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={chequeLoading || isEditDisabled}
                    >
                      {chequeLoading
                        ? (isEdit ? 'Actualizando...' : 'Guardando...')
                        : (isEdit ? 'Actualizar Cheque' : 'Guardar Cheque')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

CheckRegistrationPage.propTypes = {
  isEdit: PropTypes.bool,
};

export default CheckRegistrationPage;
